import FormField from 'components/FormField';
import { FV, isLongTermValidity, isRangeTermValidity, useTermOfValidityRender } from './share';
import { Button, Radio } from 'antd';
import dayjs from 'lib/dayjs';
import { FormOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import useMultiTimeSelect from 'components/MultiTimeSelect/useMultTimeSelect';
import { ReactNode } from 'react';
import { DatePicker } from 'components/DatePicker';

function TermOfValidity({ editable = true }: { editable?: boolean }) {
  const { t, termOfValidityRender } = useTermOfValidityRender();

  return (
    <FormField<FV['termOfValidity']>
      required
      name="termOfValidity"
      label={t('marketing.有效期')}
      validate={(value) => {
        if (isLongTermValidity(value)) {
          return undefined;
        }

        if (isRangeTermValidity(value)) {
          const {
            termRange: {
              dtRange: { startAt, endAt },
            },
          } = value;

          if (!startAt || !endAt) {
            return t('marketing.请选择起止时间');
          }
        }

        return undefined;
      }}
    >
      {({ field: { value, onChange, onBlur } }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [open, modal] = useMultiTimeSelect({
          onSelect: (termRange: any) => {
            onChange({
              ...value,
              termRange,
            } as any);
          },
        });

        const isLongTerm = isLongTermValidity(value);
        const isRangeTerm = isRangeTermValidity(value);
        const isRepeatTerm = !isLongTerm && !isRangeTerm;

        const radios = (
          <div style={{ padding: '5px 0' }}>
            <Radio
              disabled={!editable}
              checked={isLongTerm}
              onChange={({ target: { checked } }) => {
                if (checked) {
                  onChange({
                    termType: 'LONG_TERM',
                  });
                }
              }}
            >
              {t('marketing.长期')}
            </Radio>
            <Radio
              disabled={!editable}
              checked={isRangeTerm}
              onChange={({ target: { checked } }) => {
                if (checked) {
                  onChange({
                    termType: 'RANGE',
                    termRange: {
                      repeatType: 'NO_REPEAT',
                      dtRange: {
                        startAt: '',
                        endAt: '',
                      },
                    },
                  });
                }
              }}
            >
              {t('aide.固定时间段')}
            </Radio>
            <Radio
              disabled={!editable}
              checked={isRepeatTerm}
              onChange={({ target: { checked } }) => {
                if (checked) {
                  onChange({
                    termType: 'RANGE',
                    termRange: {
                      repeatType: 'DAY',
                      dayRepeat: [
                        {
                          startAt: '00:00',
                          endAt: '23:59',
                        },
                      ],
                    },
                  });
                }
              }}
            >
              {t('multiTimeSelect.循环时间段')}
            </Radio>
          </div>
        );

        const withTips = (...nodes: ReactNode[]) => {
          return (
            <div>
              <Tips>{t('aide.任务执行时间以站点时区为准')}</Tips>
              {nodes}
            </div>
          );
        };

        if (isLongTerm) {
          return withTips(radios);
        }

        if (isRangeTerm) {
          const {
            termRange: {
              dtRange: { startAt, endAt },
            },
          } = value;

          const toDay = dayjs().startOf('days');

          const format = 'YYYY-MM-DD HH:mm';

          const rangePicker = (
            <DatePicker.RangePicker
              showTime
              showSecond={false}
              disabled={!editable}
              allowClear={false}
              value={startAt && endAt ? [dayjs(`${startAt}:00`), dayjs(`${endAt}:00`)] : null}
              format={format}
              onChange={(next) => {
                if (!next) {
                  return;
                }

                const [start, end] = next;

                onChange({
                  termType: 'RANGE',
                  termRange: {
                    repeatType: 'NO_REPEAT',
                    dtRange: {
                      startAt: dayjs(start).format(format),
                      endAt: dayjs(end).format(format),
                    },
                  },
                });
              }}
              onBlur={onBlur}
              disabledDate={(date) => date.isBefore(toDay)}
            />
          );

          return withTips(radios, <Extra>{rangePicker}</Extra>);
        }

        return withTips(
          radios,
          <Extra>
            {termOfValidityRender(value)}
            {editable && (
              <Button type="text" icon={<FormOutlined />} onClick={() => open(value.termRange)} />
            )}
          </Extra>,
          modal,
        );
      }}
    </FormField>
  );
}

const Extra = styled.div`
  padding: 8px 12px;
  background: #f7f9fa;
`;

const Tips = styled.div`
  display: inline-block;
  padding: 5px 12px;
  background: #fffaf0;
`;

export default TermOfValidity;
